






















import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import { ImageIPostProperties } from "@/services/posts/postTypes/image";
import { fixedHtmlData, ContainerDimensions } from "@/services/posts/postTypes/fixedhtml";
import { Cropper } from "vue-advanced-cropper";

// Import the styles for the vue advanced cropper component
import "vue-advanced-cropper/dist/style.css";
import "vue-advanced-cropper/dist/theme.compact.css";

@Component({
    components: {
        cropper: Cropper
    }
})
export default class VueCropperAdjust extends Vue {
    // Props passed to the component
    @Prop() image!: string;
    @Prop() containerDimensions!: ContainerDimensions;
    @Prop() localImageProperties!: ImageIPostProperties;
    @Prop() localMediaProperties!: fixedHtmlData;
    @Prop() maxContainerSize!: number;
    @Prop() showBehavior!: boolean;

    // Reference to the cropper component
    @Ref("cropper") cropper!: Cropper;

    // Local properties for image or media data
    localProperties = {} as ImageIPostProperties | fixedHtmlData;

    // Watcher for the image prop to initialize the cropper when the image changes
    @Watch("image") imageChanged() {
        this.initCropper();
    }

    // load cropper when media container dimensions changes
    @Watch("containerDimensions", { immediate: true, deep: true }) containerDimensionsChanged() {
        this.initCropper();
    }

    // Initial size of the original image
    originalImageSize = {
        height: 200,
        width: 200
    }

    // Current calculated container image size
    imageSize = {
        height: 200,
        width: 200
    }

    // Aspect ratio of the channel
    channelAspectRatio = 1;

    // Ratio and factor for resizing calculations
    ratio = 1;
    factor = 1;

    // on show behavior false, override inline styles pointer-events for cropper to allow mouse clink pass through the cropper component.
    get cropperPointerEventsClass() {
        if (this.localImageProperties) { // For image Form, we don't want to disable pointer events
            return;
        }
        return {
            "disable-pointer-events": !this.showBehavior
        };
    }

    get cropperSizeCss() {
        return {
            width: `${this.imageSize.width}px`,
            height: `${this.imageSize.height}px`
        };
    }

    // Lifecycle hook to initialize the cropper
    mounted() {
        // For the image form, we have localImageProperties and for the text & image form, we have localMediaProperties
        this.localProperties = this.localImageProperties ? this.localImageProperties : this.localMediaProperties;
    }

    initCropper() {
        // Load the image to set the original image size and calculate the aspect ratio
        const imageLoader = new Image();
        imageLoader.onload = () => {
            // Set the original image size
            this.originalImageSize.width = imageLoader.width;
            this.originalImageSize.height = imageLoader.height;
            // Calculate the aspect ratio and set the image size
            this.calculateAspectRatio(this.originalImageSize.width, this.originalImageSize.height);
        };
        imageLoader.src = this.image;
    }

    // When the cropper selection is changed, update the properties to live preview in the text and image form
    change({ coordinates }: { coordinates: { width: number; height: number } }) {
        if (this.localImageProperties) {
            return;
        }
        if (coordinates.width > 0 && coordinates.height > 0 && this.localProperties.behavior === "adjust") {
            this.updateProperties();
        }
    }

    calculateAspectRatio(imageWidth: number, imageHeight: number) {
        const widthRatio = this.containerDimensions.width / imageWidth;
        const heightRatio = this.containerDimensions.height / imageHeight;

        // Calculate the ratio based on the channel width and height
        this.ratio = Math.max(widthRatio, heightRatio);

        const maxSize = Math.min(Math.max(this.containerDimensions.width, this.containerDimensions.height), this.maxContainerSize);
        // Calculate the factor based on the ratio and the max container size
        this.factor = Math.min(maxSize / (imageWidth * this.ratio), maxSize / (imageHeight * this.ratio));

        // Adjust image size based on the new ratio and factor
        this.imageSize.width = Math.ceil((imageWidth * this.factor) * this.ratio);
        this.imageSize.height = Math.ceil((imageHeight * this.factor) * this.ratio);
        this.channelAspectRatio = this.containerDimensions.width / this.containerDimensions.height;

        this.cropper.refresh(); // We have a dynamic cropper container size and cropper needs to be refreshed to adjust to the new size.

        // After the cropper is refreshed wait few milliseconds to set the coordinates to let cropper refresh properly
        setTimeout(() => {
            // load the saved cropper selection size or center the selection
            var isCurrentSavedImage = this.localProperties.position && (this.localMediaProperties ? this.localProperties.position.id === this.image : this.localProperties.position.id === this.localProperties.path);
            // TODO: position channel width should be changed to container size width and height
            if (isCurrentSavedImage && this.localProperties.position.channelWidth === this.containerDimensions.width && this.localProperties.position.channelHeight === this.containerDimensions.height) {
                if (this.localProperties.position && this.localProperties.position.cropSizeWidth && this.localProperties.position.cropSizeHeight) {
                    // load the saved coordinates
                    this.cropper.setCoordinates([
                        () => ({
                            width: this.localProperties.position.cropSizeWidth ? this.localProperties.position.cropSizeWidth / (this.factor * this.ratio) : undefined,
                            height: this.localProperties.position.cropSizeHeight ? this.localProperties.position.cropSizeHeight / (this.factor * this.ratio) : undefined
                        }),
                        () => ({
                            left: this.localProperties.position.editorX / (this.factor * this.ratio),
                            top: this.localProperties.position.editorY / (this.factor * this.ratio)
                        })
                    ]);
                }
            } else {
                // center the selection
                this.cropper.setCoordinates([
                    ({ imageSize }) => ({
                        width: imageSize.width,
                        height: imageSize.height
                    }),
                    ({ coordinates, imageSize }) => ({
                        left: imageSize.width / 2 - coordinates.width / 2,
                        top: imageSize.height / 2 - coordinates.height / 2
                    })
                ]);
            }
        }, 100);
    }

    updateProperties() {
        const cropperSelection = (this.cropper as any).stencilCoordinates;
        // emit only the properties position without updating the localProperties
        const newPosition = {
            id: this.localProperties.path ? this.localProperties.path : this.image,
            x: cropperSelection.left === 0 ? 0 : cropperSelection.left / this.factor,
            y: cropperSelection.top === 0 ? 0 : cropperSelection.top / this.factor,
            editorX: cropperSelection.left,
            editorY: cropperSelection.top,
            cropSizeWidth: cropperSelection.width ? cropperSelection.width : null,
            cropSizeHeight: cropperSelection.height ? cropperSelection.height : null,
            factor: this.factor ? this.factor : null,
            channelWidth: this.containerDimensions.width, // TODO: channel width should be changed to container size width
            channelHeight: this.containerDimensions.height, // TODO: channel height should be changed to container size height
            imgWidth: this.originalImageSize.width,
            imgHeight: this.originalImageSize.height
        };
        this.$emit("adjustPropertiesUpdated", { ...this.localProperties, position: newPosition }, this.ratio);
    }
}
